import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileText, FileImage, Download, Trash2, Eye, MessageCircle } from 'lucide-react';
import { useAuth } from '@/auth';
import { isFileTypeSupported } from '@/common/components/FilePreview';
import { getStorageUrl } from '@/common/utils';
import { PreviewArchivoDialog } from './PreviewArchivoDialog';
import type { ArchivoRecord } from '../types';

interface ArchivoItemProps {
  archivo: ArchivoRecord;
  isDeleting: boolean;
  onDelete: (archivoId: number) => Promise<void>;
  onDownload: (archivo: ArchivoRecord) => Promise<void>;
  onSendWhatsApp?: (archivo: ArchivoRecord) => void;
  canSendWhatsApp?: boolean;
}

export const ArchivoItem: React.FC<ArchivoItemProps> = ({
  archivo,
  isDeleting,
  onDelete,
  onDownload,
  onSendWhatsApp,
  canSendWhatsApp = false,
}) => {
  const { user } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const canDelete =
    user?.id === archivo.uploadedBy.id ||
    user?.permissions?.includes('otros_tramites_archivos.manage');

  const FileIcon = archivo.mimeType.includes('pdf') ? FileText : FileImage;

  // Check if file can be previewed (convertir URL relativa a absoluta, remover /api inicial si existe)
  const relativePath = archivo.fileUrl.replace(/^\/api/, '');
  const fullUrl = getStorageUrl(relativePath);
  const canPreview = isFileTypeSupported(fullUrl);

  const handleDelete = async () => {
    await onDelete(archivo.id);
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded">
              <FileIcon className="w-5 h-5 text-gray-600" />
            </div>

            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 truncate" title={archivo.fileName}>
                {archivo.fileName}
              </h4>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-600">
                <span>{archivo.formattedSize}</span>
                <span>•</span>
                <span>Subido por {archivo.uploadedBy.name}</span>
                <span>•</span>
                <span>{new Date(archivo.createdAt).toLocaleDateString('es-ES')}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {canPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewDialogOpen(true)}
                  title="Vista previa"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDownload(archivo)}
                title="Descargar"
              >
                <Download className="w-4 h-4" />
              </Button>

              {canSendWhatsApp && onSendWhatsApp && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSendWhatsApp(archivo)}
                  title="Enviar por WhatsApp"
                  className="text-green-600 hover:text-green-700"
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
              )}

              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  disabled={isDeleting}
                  onClick={() => setDeleteDialogOpen(true)}
                  title="Eliminar"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar archivo?</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. El archivo "{archivo.fileName}" será eliminado
              permanentemente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PreviewArchivoDialog
        archivo={archivo}
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
      />
    </>
  );
};
